import React, { useState, useEffect, useRef } from "react";
import {
  WarsEvents,
  LoadSceneEvent,
  WarsScenes,
  LoadingEvent,
} from "@playroomkit/pr-wars/events";
import JoinGameModal from "@components/JoinGameModal";
import ScalableWrapper from "@components/ScalableWrapper";
import View from "@components/View";
import Button from "@components/Button";
import MainMenuBackground from "@components/MainMenuBackground";
import { images, videos } from "@utils/media";
import { getUrlHashParameter } from "@utils/helpers";
import "./StartMenu.css";
import { platform } from "@playroomkit/pr-common";
import { useAuth } from "@src/contexts/AuthProvider";
import { LoadingType, utils } from "@playroomkit/pr-wars/utils";
import { useTranslation } from "react-i18next";
import GameInstructionsCard from "@components/GameInstructionsCard";
import BaseButton from "@components/Button";

interface StartMenuProps {
  onCreateGame: (roomCode: string | null, musicContext: AudioContext) => void;
}

const StartMenu: React.FC<StartMenuProps> = ({ onCreateGame }) => {
  const { userInfo, logout } = useAuth();

  const [showUI, setShowUI] = useState(true);
  const [loginAsGuest, setLoginAsGuest] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [isJoinModalOpen, setIsJoinModalOpen] = useState(false);
  const bgRef = useRef<HTMLVideoElement | HTMLImageElement | null>(null);
  const isLoadedRef = useRef<boolean>(false);
  const { t } = useTranslation(["main"]);
  const runMobileWeb = utils.isRunningInMobile() && !platform.isRunningInDiscord;
  useEffect(() => {
    const handleLoadedData = () => {
      if (interval) {
        clearInterval(interval);
      }

      if (!videoLoaded) {
        document.dispatchEvent(new CustomEvent("appReadyEvent", {}));
      }

      setVideoLoaded(true);
    };

    const bgElement = bgRef.current;
    if (!bgElement) return;

    bgElement.addEventListener("loadeddata", handleLoadedData);
    bgElement.addEventListener("load", handleLoadedData);

    const interval = setTimeout(handleLoadedData, 3000);

    const handleLoadSceneEvents = (event: Event) => {
      let e = event as LoadSceneEvent,
        display = e.sceneName === WarsScenes.Title;

      setShowUI(display);
    };

    document.addEventListener(WarsEvents.LoadSceneEvent, handleLoadSceneEvents);

    return () => {
      document.removeEventListener(
        WarsEvents.LoadSceneEvent,
        handleLoadSceneEvents
      );
      bgElement.removeEventListener("loadeddata", handleLoadedData);
    };
  }, []);

  useEffect(() => {
    if (isLoadedRef.current) {
      return;
    }

    if (platform.isRunningInDiscord) {
      isLoadedRef.current = true;
      setTimeout(() => createGame(null), 4000);
    }
    else {
      const roomHashCode = getUrlHashParameter();
      if (roomHashCode.length === 4) {
        createGame(roomHashCode, true);
      }
    }


  }, []);

  const createGame = async (roomCode: string | null, skipResumingAudioContext: boolean = false) => {
    document.dispatchEvent(
      new LoadingEvent(10, t("loading-message-3"), LoadingType.MainMenu, false)
    );

    const musicContext = new AudioContext();
    // skip resuming audio context if the player is joining a game via room link
    // otherwise, it never lets player join the game via room link.
    if (!skipResumingAudioContext) {
      await musicContext.resume().catch((error) => { console.warn(error?.message); });
    }

    onCreateGame(roomCode, musicContext);

    setShowUI(false);
  };

  const handleCreateGame = async () => {
    await createGame(null);
  };

  const handleOpenJoinModal = () => {
    setIsJoinModalOpen(true);
  };

  const handleCloseJoinModal = () => {
    setIsJoinModalOpen(false);
  };

  const loginWithDiscord = () => {
    window.location.href = `${
      import.meta.env.VITE_PLAYROOM_BASE_URL
    }/auth/discord/login?gameId=${import.meta.env.VITE_GAME_ID}`;
  };

  const playAsGuest = () => {
    setLoginAsGuest(true);
  };

  const renderLogout = () => {
    if (!userInfo?.user.id) {
      return null;
    }

    
    return (
      <BaseButton
        className="relative rounded-3xl border-dark border-solid border-2 logout-button py-8"
        onClick={logout}
      >
        <div className="avatar-screen-img">
          {userInfo?.avatarSrc ?
            <img
              src={userInfo?.avatarSrc}
              alt="avatar"
              width="100%"
              height="100%"
              style={{ borderRadius: "50%" }}
              draggable={false}
              loading="lazy"
            />
          :
            <View className="flex items-center justify-center w-9 h-9 rounded-full bg-blue-400 font-mono text-white text-2xl mr-4 uppercase">
              <p className="">{userInfo?.user?.username.slice(0, 1)}</p>
            </View>
          }
        </div>
        <p className="text-white font-[slackey]">
          {" "}
          {userInfo.user.username} (Logout)
        </p>
      </BaseButton>
    );
    
  };

  const renderMenuOptions = () => {
    if (platform.isRunningInDiscord) {
      return null;
    }

    if (!userInfo?.user.id && !loginAsGuest) {
      return (
        <View className="flex flex-col items-center justify-end w-full h-full pb-24">
          <Button
            className="!w-96 !h-24 lg:!w-80 lg:!h-20 bg-transparent border-none menu-button start-menu-button"
            onClick={loginWithDiscord}
          >
            <img
              src={images.LoginWithDiscord}
              alt="login-with-discord"
              className="w-full h-full object-contain"
            />
          </Button>
          <Button
            className="md:!w-96 md:!h-24 lg:!w-80 lg:!h-20 bg-transparent border-none menu-button start-menu-button"
            onClick={playAsGuest}
          >
            <img
              src={images.PlayAsGuest}
              alt="play-as-guest"
              className="w-full h-full object-contain"
            />
          </Button>
        </View>
      );
    } else {
      return (
        <>
          <View className="buttons-container">
            <Button
              className="menu-button create-menu-button"
              onClick={handleCreateGame}
            >
              <img
                src="img/UI/Create_Room_Blur.webp"
                alt="blur-create-room"
                className="create-room-blur icon"
              />
              <img
                src="img/UI/Create_Room_Focus.webp"
                alt="focus-create-room"
                className="create-room-focus icon"
              />
              <img
                src="img/UI/Create_Room_Focus_Stroke.webp"
                alt="active-create-room-stroke"
                className="create-room-stroke"
              />
            </Button>
            <Button
              className="menu-button join-menu-button"
              onClick={handleOpenJoinModal}
            >
              <img
                src="img/UI/Join_Room_Blur.webp"
                alt="blur-join-room"
                className="join-room-blur icon"
              />
              <img
                src="img/UI/Join_Room_Focus.webp"
                alt="focus-join-room"
                className="join-room-focus icon"
              />
              <img
                src="img/UI/Join_Room_Focus_Stroke.webp"
                alt="active-join-room-stroke"
                className="join-room-stroke"
              />
            </Button>
          </View>
          <View className="logout-container">{renderLogout()}</View>
        </>
      );
    }
  };

  return (
    <>
      {showUI && (
        <View className="start-menu" style={runMobileWeb ? {
          background:  "linear-gradient(180deg, #1a2c7c 0%, #2d48c4 100%)"
        } : {}}>
          <MainMenuBackground
            ref={bgRef}
            source={videos.MainMenuVideo}
            placeholder={images.MainMenuBackground}
            mobileBg={images.MainMenuBackgroundMobile}
          />
          { !runMobileWeb && !platform.isRunningInDiscord ?
          <>
            <ScalableWrapper
              ratio="16_9"
              anchor="right"
              maintainAspectRatioBy={{
                ratioBy: ["width", "height"],
                breakpointByAspectRatio: 1.5,
              }}
              containerStyle={{ zIndex: 9999 }}
            >
              <View className={`w-full h-full absolute top-0 z-lg flex flex-col justify-center items-end hidden md:flex`}>
                <View className="relative w-80 h-80 flex flex-col justify-center items-center -right-28">
                  <img
                    src={images.BrainImage}
                    alt="brain"
                    className="w-full h-full object-contain"
                    width={100}
                    height={100}
                  />
                </View>
              </View>
            </ScalableWrapper>

            <ScalableWrapper
              ratio="16_9"
              anchor="left"
              maintainAspectRatioBy={{
                ratioBy: ["width", "height"],
                breakpointByAspectRatio: 1.5,
              }}
              containerStyle={{ zIndex: 9999 }}
            >
              <View className={`w-full h-full absolute top-0 -left-8 z-lg flex flex-col justify-center items-start hidden md:flex`}>
                <View className="relative bg-transparent flex flex-col border-solid justify-center items-center border-4 border-[#F72EC5] rounded-2xl scale-[0.72]">
                  <h1 className="absolute -top-6 w-full text-3xl font-[Slackey] bg-[#F72EC5] text-white text-center py-2 rounded-tl-2xl rounded-tr-2xl">
                    {t("how_to_play_heading")}
                  </h1>
                  <View className="w-[350px] h-[425px] mt-12 text-center">
                    <GameInstructionsCard/>
                  </View>
                </View>
              </View>
            </ScalableWrapper>
          </>
          :
          null
          }
          
          <ScalableWrapper maintainAspectRatioBy="height" >
          { !runMobileWeb ? <View className={`title-container`}>{renderMenuOptions()}</View> : null} 
            {runMobileWeb && 
              <View className={`mobile-content`}> 
                <p className="text-white text-center text-[12px] font-[slackey] px-4"> {t('mobile_browser_message')} </p>
                <Button
                  className="w-full h-full bg-transparent border-none"
                  onClick={() => {
                    window.open("https://discord.com/activities/1294399938553319485?referrer_id=1216985806237991013", "_blank");
                  }}
                >
                  <img
                    src="img/UI/Discord_Mobile_Button.webp"
                    alt="blur-join-room"
                    width={200}
                  />
                </Button>
              </View>
            }
          </ScalableWrapper>
          
          <JoinGameModal
            isOpen={isJoinModalOpen}
            onClose={handleCloseJoinModal}
            onJoinGame={createGame}
          />
        </View>
      )}
    </>
  );
};

export default StartMenu;
